
import {AlternateRenderingPropertyRoot} from "kistenkonfigurator/build/product/AlternateRenderingProductModel";
import {parseProductDescription} from "kistenkonfigurator/build/util/parse-product-description";
import {toPrimitiveValue} from "kistenkonfigurator/build/util/primitive-types-util";
import {parseIntWithDefault} from "kistenkonfigurator/build/util/util";
import {isEqual} from "lodash";
// @ts-ignore
import URI from "urijs";
import Vue from "vue";
import {ImageGalleryImageModel} from "../../../model/ImageGalleryImageModel";
import {ProductModelFactory} from "../../../model/product/ProductModelFactory";
import {
  getProductOverviewImage,
  ProductConfiguratorUIModel,
  QUANTITY_QUERY_PROPERTY,
  toDefaultConfigurationObject,
  VIEW_ONLY_QUERY_PROPERTY,
} from "../../../model/ProductBusinessLogic";
import {ProductDTO} from "../../../model/rest/data-contracts";

interface State {
  productConfiguratorModel: ProductConfiguratorUIModel,
  images: Array<ImageGalleryImageModel>,
  product: ProductDTO,
}

export default Vue.extend({
  components: {
    ProductConfigurator: () => import("../../../components/configurator/ProductConfigurator.vue"),
  },
  async asyncData(context) {
    const articleNumber = context.params.articleNumber;
    const product = await context.$bl.productService.getByArticleNumber(articleNumber);
    const result = await context.$bl.productService.getProductGalleryImageList(product);
    return {
      images: result,
      product,
    };
  },
  data(): State {
    return {
      productConfiguratorModel: {
        configuration: this.$route.query as any,
        configuratorConfiguration: {
          productConfiguration: {},
          completeConfiguration: {},
        },
      },
      images: [],
      product: ProductModelFactory.createEmptyOverviewProduct(),
    };
  },
  beforeRouteUpdate(to: any, from: any, next: any): void {
    // vue caches components as such we don't get notified if a query string changes by clicking a link
    // (like a product link inside the cart)
    // Detect if we are on the same page, but query changes
    if (to.name === from.name && !isEqual(to.query, from.query)) {
      (this.$refs.productConfigurator as any).updateConfiguration(to.query);
    }
    next();
  },
  computed: {
    isAdmin(): boolean {
      return this.$auth.hasAdminPermission();
    },
    initialQuantity(): number {
      return parseIntWithDefault(this.$route.query[QUANTITY_QUERY_PROPERTY]?.toString(), 1);
    },
    propertyRoot(): AlternateRenderingPropertyRoot {
      return AlternateRenderingPropertyRoot.PRODUCT_MODEL;
    },
    articleNumber(): string {
      return this.$route.params.articleNumber;
    },
    isViewOnly(): boolean {
      const value = this.$route.query.viewOnly?.toString();
      return toPrimitiveValue(value ?? "false") as boolean;
    },
  },
  watch: {
    productConfiguratorModel: {
      handler(newVal: ProductConfiguratorUIModel, oldVal: ProductConfiguratorUIModel) {
        if (newVal.configuration !== oldVal.configuration) {
          this.updateConfiguration();
        }
      },
      deep: true,
    },
  },
  methods: {
    updateConfiguration(): void {
      const state = window.history.state;
      const uri = URI(window.location.href);
      const queryMap = uri.query(true);

      const simpleValue: boolean | undefined = queryMap[VIEW_ONLY_QUERY_PROPERTY] != null ? queryMap[VIEW_ONLY_QUERY_PROPERTY] : undefined;
      uri.search(this.productConfiguratorModel.configuration);
      if (simpleValue != null) {
        uri.addQuery(VIEW_ONLY_QUERY_PROPERTY, simpleValue);
      }
      window.history.replaceState(state, state.title, uri.toString());
    },
  },
  head(): any {
    const productAttributes = toDefaultConfigurationObject(this.product);
    return {
      title: this.$t("pages.product.pageTitle", {
        name: this.product.name,
        domain: this.$config.domain,
      }),
      meta: [
        // hid is used as unique identifier. Do not use `vmid` for it as it will not work
        {
          hid: "description",
          name: "description",
          content: this.product.description != null ? parseProductDescription(toDefaultConfigurationObject(this.product), this.product.description) : "",
        },
      ],
      script: [
        {
          type: "application/ld+json",
          json: {
            "@context": "https://schema.org/",
            "@type": "Product",
            name: this.product.name || "",
            sku: this.product.articleNumber,
            mpu: this.product.articleNumber,
            gtin: productAttributes.gtin?.toString() ?? "",
            description: this.product.description != null ? parseProductDescription(productAttributes, this.product.description) : "",
            image: encodeURI(getProductOverviewImage(this.$baseUrl(), this.product)),
            condition: "new",
            offers: {
              "@type": "Offer",
              availability: "https://schema.org/InStock",
              price: this.product.price != null ? this.product.price : 0.0,
              priceCurrency: "CHF",
            },
          },
        },
      ],
    };
  },
});

